import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import LayoutSingleView from '../../common/layout/LayoutSingleView'
import { MenuItems } from '../../../enums/MenuItems'
import Section from '../../common/sections/Section'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import roleApi from '../../../api/role/RoleApi'
import { Pages } from '../../../Pages'
import Form from '../../common/form/Form'
import TextInput from '../../common/form/inputs/TextInput'
import DropDown, { Item } from '../../common/form/inputs/DropDown'
import GetCountryFlag from '../../common/location/GetCountryFlag'
import ContractTypeBadge from '../ContractTypeBadge'
import { ContractType } from '../../../enums/ContractType'
import TextArea from '../../common/form/inputs/TextArea'
import { UpdateRoleReq } from '../../../api/role/models/updateRole/UpdateRoleReq'
import { UK_CITIES } from '../../../constants/cities'

// Zod schema
const roleSchema = z.object({
    jobTitle: z.string().min(1, 'Job title is required'),
    jobDescription: z.string().min(1, 'Job description is required'),
    location: z.string().min(1, 'Location is required'),
    contractType: z.string().min(1, 'Contract type is required'),
})

type RoleFormData = z.infer<typeof roleSchema>

export default function UpdateRole(): React.JSX.Element {
    const navigate = useNavigate()
    const [apiError, setApiError] = useState<string | undefined>(undefined)
    const [searchParams] = useSearchParams()
    const roleId = searchParams.get('id')
    const returnToParam = searchParams.get('returnTo')
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const safeReturnTo = useMemo(() => {
        const fallback = roleId ? `${Pages.ViewRole}?id=${roleId}` : Pages.Roles

        if (!returnToParam) {
            return fallback
        }

        try {
            const decoded = decodeURIComponent(returnToParam)
            if (decoded.startsWith('/')) {
                return decoded
            }
        } catch (error) {
            console.warn('Invalid returnTo param provided to UpdateRole', error)
        }

        return fallback
    }, [returnToParam, roleId])

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        setValue,
        watch,
        reset,
    } = useForm<RoleFormData>({
        resolver: zodResolver(roleSchema),
        defaultValues: {
            location: 'United Kingdom',
            contractType: 'permanent',
        },
    })

    const selectedLocation = watch('location')
    const selectedContractType = watch('contractType')

    // ✅ Build dropdown items
    const locationItems: Item[] = UK_CITIES.map((city) => ({
        id: city,
        content: (
            <div className="flex items-center gap-2">
                <GetCountryFlag location={city} showLabel={false} />
                <span>{city}</span>
            </div>
        ),
    }))

    const contractTypeItems: Item[] = [
        {
            id: 'permanent',
            content: (
                <ContractTypeBadge contractType={ContractType.Permanent} />
            ),
        },
        {
            id: 'contract',
            content: <ContractTypeBadge contractType={ContractType.Contract} />,
        },
    ]

    // ✅ Resolve the selected item from the list
    const selectedLocationItem = locationItems.find(
        (i) => i.id === selectedLocation,
    ) || {
        id: '',
        content: <span className="text-gray-400">Select Location</span>,
    }

    const selectedContractItem = contractTypeItems.find(
        (i) => i.id === selectedContractType,
    ) || {
        id: '',
        content: <span className="text-gray-400">Select Contract Type</span>,
    }

    // Handlers
    const handleLocationChange = (item: Item) => {
        setValue('location', item.id)
    }

    const handleContractTypeChange = (item: Item) => {
        setValue('contractType', item.id)
    }

    // Submit
    const onSubmit = async (data: RoleFormData) => {
        setApiError(undefined)

        try {
            const req: UpdateRoleReq = {
                roleId: roleId!,
                jobTitle: data.jobTitle,
                jobDescription: data.jobDescription,
                location: data.location,
                contractType: data.contractType,
            }
            await roleApi.updateRole(req)
            navigate(safeReturnTo)
        } catch (error) {
            console.error(error)
            setApiError(
                error instanceof Error
                    ? error.message
                    : 'An unknown error occurred. Please try again later.',
            )
        }
    }

    // Fetch role data
    const refreshRole = useCallback(async () => {
        if (!roleId) {
            return
        }

        try {
            const { role } = await roleApi.getLite({
                roleId,
            })
            reset({
                jobTitle: role.jobTitle,
                jobDescription: role.jobDescription || '',
                location: role.location || 'United Kingdom',
                contractType: role.contractType || 'permanent',
            })
        } catch (error) {
            if (
                error instanceof Error &&
                error.message.includes('Role not found')
            ) {
                navigate(Pages.Roles, { replace: true })
                return
            }
            const errMsg =
                error instanceof Error
                    ? error.message
                    : 'Failed to load role information.'
            setApiError(errMsg)
        } finally {
            setIsLoading(false)
        }
    }, [navigate, reset, roleId])

    useEffect(() => {
        refreshRole()
    }, [roleId, refreshRole])

    // Focus on Job Title input field on component mount
    useEffect(() => {
        const jobTitleInput = document.getElementById(
            'jobTitle',
        ) as HTMLInputElement | null
        if (jobTitleInput) {
            jobTitleInput.focus()
        }
    }, [isLoading])

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Roles}>
            <Section title="Update Role" titleType="h2">
                <LoadingSpinner
                    show={isSubmitting || isLoading}
                    fullScreen={true}
                />

                <Form
                    handleSubmit={handleSubmit}
                    onSubmit={onSubmit}
                    apiError={apiError}
                    entityName="role"
                    cancelTo={safeReturnTo}
                    isSubmitting={isSubmitting}
                    className="mt-4"
                >
                    <TextInput
                        id="jobTitle"
                        name="Job Title"
                        placeholder="Job Title"
                        register={register}
                        errors={errors}
                    />

                    <DropDown
                        id="location"
                        selected={selectedLocationItem}
                        name="Location"
                        placeholder="Select Location"
                        onChange={handleLocationChange}
                        items={locationItems}
                        error={errors.location?.message}
                    />

                    <DropDown
                        id="contractType"
                        selected={selectedContractItem}
                        name="Contract Type"
                        placeholder="Select Contract Type"
                        onChange={handleContractTypeChange}
                        items={contractTypeItems}
                        error={errors.contractType?.message}
                    />

                    <TextArea
                        id="jobDescription"
                        name="Job Description"
                        placeholder="Job Description..."
                        register={register}
                        errors={errors}
                        rows={10}
                    />
                </Form>
            </Section>
        </LayoutSingleView>
    )
}
